<script setup lang="ts">
import { AccountIcon, ActionButton, Dialog, Select, StatusTag, TextField } from '@taler/ui'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ACCOUNT_COLOR_PRESETS, ACCOUNT_ICONS, type UpdateAccountInput } from '../internal/account-form.ts'
import type { AccountRow } from '../internal/account-tree.ts'

const props = defineProps<{
  open: boolean
  row: AccountRow | undefined
  isSubmitting: boolean
  hasError: boolean
}>()

const emit = defineEmits<{ submit: [input: UpdateAccountInput]; close: [] }>()

const { t } = useI18n()

const firstFieldRef = useTemplateRef<InstanceType<typeof TextField>>('firstField')

const nameEn = ref('')
const nameHu = ref('')
const icon = ref<UpdateAccountInput['icon']>('CASH')
const color = ref('#3b82f6')

watch(
  () => [props.open, props.row?.id] as const,
  () => {
    if (!props.open || props.row === undefined) return
    nameEn.value = props.row.names['en']?.name ?? ''
    nameHu.value = props.row.names['hu']?.name ?? ''
    icon.value = (props.row.icon as UpdateAccountInput['icon']) ?? 'CASH'
    color.value = props.row.color
    void nextTick(() => firstFieldRef.value?.focus())
  },
)

const accountIconOptions = computed(() =>
  ACCOUNT_ICONS.map((value) => ({ value, label: t(`accounts.iconNames.${value}`) })),
)

function onSubmit(): void {
  if (props.row === undefined) return
  emit('submit', {
    id: props.row.id,
    nameEn: nameEn.value.trim(),
    nameHu: nameHu.value.trim(),
    icon: icon.value,
    color: color.value,
  })
}
</script>

<template>
  <Dialog v-if="row" :open="open" :label="t('accounts.editAccount')" @close="emit('close')">
    <form
      class="flex max-h-[85vh] w-full flex-col gap-[var(--space-cell)] overflow-y-auto p-[var(--space-block)]"
      @submit.prevent="onSubmit"
    >
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('accounts.editAccount') }}
      </h2>

      <StatusTag v-if="hasError" tone="danger" role="alert">
        {{ t('accounts.updateErrorGeneral') }}
      </StatusTag>

      <fieldset
        class="flex flex-col gap-[var(--space-inline)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] p-[var(--space-cell)]"
      >
        <legend
          class="px-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
        >
          {{ t('accounts.names') }}
        </legend>
        <TextField
          ref="firstField"
          v-model="nameEn"
          :label="t('accounts.nameEn')"
          label-size="small"
          required
          :maxlength="200"
        />
        <TextField
          v-model="nameHu"
          :label="t('accounts.nameHu')"
          label-size="small"
          required
          :maxlength="200"
        />
      </fieldset>

      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
          t('accounts.icon')
        }}</span>
        <div class="flex items-center gap-[var(--space-inline)]">
          <span
            class="flex h-[var(--density-control)] w-[var(--density-control)] shrink-0 items-center justify-center rounded-[var(--radius-control)]"
            :style="{ backgroundColor: `${color}22`, color }"
          >
            <AccountIcon :icon="icon" :label="t(`accounts.iconNames.${icon}`)" />
          </span>
          <Select v-model="icon" :options="accountIconOptions" />
        </div>
      </label>

      <div class="flex flex-col gap-[var(--space-inline)]">
        <label class="flex items-center gap-[var(--space-inline)]">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('accounts.color')
          }}</span>
          <input
            v-model="color"
            type="color"
            :aria-label="t('accounts.color')"
            class="h-[var(--density-control)] w-12 rounded-[var(--radius-control)]"
          />
        </label>
        <div>
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ t('accounts.presetColors') }}
          </p>
          <div class="mt-[var(--space-hair)] flex flex-wrap gap-[var(--space-hair)]">
            <button
              v-for="preset in ACCOUNT_COLOR_PRESETS"
              :key="preset"
              type="button"
              :aria-label="preset"
              :aria-pressed="color === preset"
              class="h-6 w-6 rounded-full border transition-transform duration-[var(--motion-fast)]"
              :class="
                color === preset
                  ? 'scale-110 border-[color:var(--color-text)]'
                  : 'border-[color:var(--color-border)]'
              "
              :style="{ backgroundColor: preset }"
              @click="color = preset"
            />
          </div>
        </div>
      </div>

      <div class="mt-[var(--space-hair)] flex justify-end gap-[var(--space-inline)]">
        <ActionButton variant="secondary" type="button" @click="emit('close')">
          {{ t('accounts.cancel') }}
        </ActionButton>
        <ActionButton type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? t('accounts.updating') : t('accounts.update') }}
        </ActionButton>
      </div>
    </form>
  </Dialog>
</template>
